import React, { useEffect, useState } from 'react';
import clsx from 'clsx';
import styles from './styles.module.scss';
import {
  addDocumentFileToCache,
  clearExpiredDocumentFiles,
  getDocumentFileFromCache,
} from '../../../local-store/idb/document-file-store';

interface ImageWithTokenProps {
  fileUrl: string;
}

const PdfWithToken: React.FC<ImageWithTokenProps> = ({ fileUrl }) => {
  const [fileSrc, setFileSrc] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchImage = async () => {
      try {
        setError(() => null);
        setFileSrc(() => null);
        // Clear expired images
        await clearExpiredDocumentFiles();

        // Check if the image is already cached
        const cachedBlob = await getDocumentFileFromCache(fileUrl);

        if (cachedBlob) {
          setFileSrc(() => {
            return URL.createObjectURL(cachedBlob);
          });
          return;
        }

        // Fetch the image from the server
        const response = await fetch(fileUrl);

        if (!response.ok) {
          setFileSrc(() => null);
          throw new Error(`Failed to fetch image: ${response.statusText}`);
        }

        const blob = await response.blob();
        const objectURL = URL.createObjectURL(blob);

        // Cache the fetched image
        await addDocumentFileToCache(fileUrl, blob);

        setFileSrc(() => objectURL);
      } catch (err) {
        setFileSrc(() => null);
        setError(err instanceof Error ? err.message : 'Unknown error occurred');
      }
    };

    fetchImage();

    // Cleanup object URL to prevent memory leaks
    return () => {
      if (fileSrc) {
        URL.revokeObjectURL(fileSrc);
      }
    };
  }, [fileUrl]);

  if (error) {
    return (
      <div className={styles.root}>
        <p>Error loading file: {error}</p>
      </div>
    );
  }

  if (!fileSrc) {
    return (
      <div className={styles.root}>
        <p>Loading file...</p>
      </div>
    );
  }

  return (
    <div className={clsx(styles.root)}>
      <iframe title="Document" src={fileSrc} className={styles.pdf} />
    </div>
  );
};

export default PdfWithToken;
